import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl } from 'react-intl'

import { useUserContext } from '../../auth/UserProvider'
import useTranslations from '../../i18n/useTranslations'
import useEnhancedFormik from '../../hooks/useEnhancedFormik'
import ConfirmationWithErrorModal from '../../ui/modals/confirmationWithError'
import ActionButtons from '../../ui/views/shippingSend/ActionButtons'
import ShippingSendForm from '../../ui/views/shippingSend/FormWrapper'
import Step2DestinationUI from '../../ui/views/shippingSend/Step2Destination'
import { STEPS, ADDRESS_STATES } from '../../common/constants'
import countryTelephoneData from 'country-telephone-data'
import {
  shippingSend2ToPropTypes,
  nullablePropType,
} from '../../common/propTypes'
import {
  makeFieldErrorsPropType,
  makeValuesPropType,
} from '../../common/formikPropTypes'
import { generateTouchedObject } from '../../libs/formik'

import useDebounce from './useDebounce'
import calculateProvinceAndLocalities, {
  GEOCODING_ERRORS,
} from './calculateProvinceAndLocalities'
import { formatCentreAddress } from './formatters'
import useShippingSendStep from './useShippingSendStep'
import { DEBOUNCED_POSTAL_CODE_DELAY } from './Step1Origin'
import { useDestinationCountries } from '../../services/destinationCountries'
import useDeliveryPoints from '../../services/deliveryPoints'
import { isValidPhoneNumber } from 'react-phone-number-input'

const getDestinationSchema = () => {
  return Yup.object().shape({
    addressState: Yup.string().oneOf(Object.keys(ADDRESS_STATES)).required(),
    address: Yup.object().when('addressState', {
      is: ADDRESS_STATES.NEW,
      then: Yup.object().shape({
        country: Yup.object().shape({
          label: Yup.string().required(),
          code: Yup.string().required(),
        }),
        address: Yup.string().required(),
        name: Yup.string().required(),
        phonePrefix: Yup.string(),
        phone: Yup.string()
          .test(
            'isValidPhoneNumber',
            {
              key: 'shipping-send-form:new-address:phone-format-or-length-error',
            },
            function (value) {
              if (!value) return true
              const regex = /^\d+$/
              return regex.test(value.replace(/\s+/g, ''))
            },
          ),
        /*.required()
          .test(
            'isValidPhoneNumber',
            {
              key: 'shipping-send-form:new-address:phone-format-or-length-error',
            },
            function (value, schema) {
              const phonePrefix = schema.parent.phonePrefix
              const fullPhoneNumber = `${phonePrefix}${value}`
              return isValidPhoneNumber(fullPhoneNumber)
            },
          )*/ email: Yup.string().email(),
        stair: Yup.string(),
        floor: Yup.string(),
        letter: Yup.string(),
        postalCode: Yup.string().when('country.code', {
          is: 'ES',
          then: Yup.string()
            .length(5, {
              key: 'shipping-send-form:origin:postal-code:invalid-format',
            })
            .required({
              key: 'error:field:postal-code:required',
            }),
          otherwise: Yup.string().when('country.code', {
            is: 'PT',
            then: Yup.string()
              .matches(/^(?:\d{4}|\d{4}-\d{3})$/, {
                key: 'shipping-send-form:origin:postal-code:invalid-format',
              })
              .required({
                key: 'error:field:postal-code:required',
              }),
            otherwise: Yup.string().required({
              key: 'error:field:postal-code:required',
            }),
          }),
        }),
        province: Yup.string(),
        city: Yup.string().required(),
        schedule: Yup.array().nullable(),
        contactPerson: Yup.string(),
      }),
    }),
    comments: Yup.string(),
    addressBook: Yup.object().nullable(),
    newAddress: Yup.object().nullable(),
  })
}

const DEFAULT_PHONE_PREFIX = '+34'

const INITIAL_VALUES = {
  addressState: ADDRESS_STATES.NEW,
  addressBook: null,
  newAddress: null,
  selectedDeliveryPoint: undefined,
  deliveryPointAddress: undefined,
  confirmedAddress: undefined,
  address: {
    country: { code: 'ES', label: 'España' },
    name: '',
    address: '',
    phone: '',
    phonePrefix: DEFAULT_PHONE_PREFIX,
    email: '',
    stair: '',
    floor: '',
    letter: '',
    postalCode: '',
    province: '',
    city: '',
    contactPerson: '',
    hasSeveralLocalities: false,
    localitiesOptions: null,
    schedule: null,
  },
  comments: '',
}

const Step2Destination = ({
  centreCode,
  savedValues,
  originCentreId,
  originSource,
  activeStep,
  serverFieldErrors,
  hasOtherError,
  onSubmitSuccess,
  onCancel,
  onSetDirty,
  hasConfirmation,
  onCancelConfirmation,
  onAcceptConfirmation,
  isPudo,
}) => {
  const t = useTranslations()
  const intl = useIntl()
  const { centres } = useUserContext()
  const { data: countries } = useDestinationCountries()

  // NOTE: mirar el comentario de este mismo estado en el archivo
  // `src/context/ShippingSend/Step1Origin/index.jsx`.
  const [zeroResultsGeocode, setZeroResultsGeocode] = useState(false)

  const [initialLoad, setInitialLoad] = useState(true)

  const [error, setError] = useState(false)
  const [fromSearch, setFromSearch] = useState(false)

  const handlePickUpErrors = hasErrors => {
    setError(hasErrors)
  }

  const getPhoneInfo = phoneSource => {
    let phone = phoneSource
    let prefix = '+34'

    if (phone?.length && phone[0] === '+') {
      let findPrefix = countryTelephoneData.allCountries.filter(
        ({ dialCode }) => phone.startsWith('+' + dialCode),
      )
      phone = phone.replace(
        '+' + findPrefix[findPrefix?.length - 1]?.dialCode,
        '',
      )
      prefix = '+' + findPrefix[findPrefix?.length - 1]?.dialCode
    }
    return { prefix: prefix, phone: phone }
  }

  const formik = useFormik({
    validateOnChange: false,
    validationSchema: getDestinationSchema(),
    initialValues: savedValues ?? INITIAL_VALUES,
    initialTouched: !!savedValues
      ? generateTouchedObject(shippingSend2ToPropTypes, savedValues)
      : undefined,
    onSubmit: values => {
      return onSubmitSuccess({ ...values })
    },
  })

  const deliveryPoints = useDeliveryPoints(
    {
      lat: formik.values.deliveryPointAddress?.location.lat,
      lng: formik.values.deliveryPointAddress?.location.lng,
      address: formik.values.deliveryPointAddress?.address,
      city: 'Madrid',
      postalCode:
        formik.values.deliveryPointAddress?.postalCode?.postalCode ??
        formik.values.deliveryPointAddress?.postalCode,
      countryCode: formik.values.deliveryPointAddress?.country.code,
      dimensions: [
        {
          width: 0,
          height: 0,
          depth: 0,
          magnitude: 'CM',
        },
      ],
      range: 3,
    },
    t,
  )

  const confirmAddress = address => {
    formik.setValues(values => ({
      ...values,
      confirmedAddress: address,
    }))
  }

  const changeDeliveryPointAddress = address => {
    formik.setValues(values => ({
      ...values,
      deliveryPointAddress: address,
    }))
  }

  // const [selectedDeliveryPoint, setSelectedDeliveryPoint] = useState(
  //   savedValues && savedValues.addressState === ADDRESS_STATES.AT_PICKUP_POINT
  //     ? {
  //         name: savedValues.address.name,
  //         detailedAddress: {
  //           address: 'AVENIDA DE LA CONSTITUCIÓN 20',
  //           address_2: 'TORREJÓN DE ARDOZ',
  //           country_code: 'ES',
  //           gps_latitude: 40.4547355,
  //           gps_longitude: -3.4898714,
  //           postal_code: '28850',
  //         },
  //         address: savedValues.address.address,
  //         phoneNumber: savedValues.address.phone,
  //         schedule: savedValues.address.schedule,
  //       }
  //     : undefined,
  // )

  const enhancedFormik = useEnhancedFormik(shippingSend2ToPropTypes, formik, {
    fieldErrors: serverFieldErrors,
  })

  const { handleDiscardConfirmation, hasServerError, hasAnyError } =
    useShippingSendStep({
      formik,
      enhancedFormik,
      onSetDirty,
      hasConfirmation,
      onAcceptConfirmation,
      serverFieldErrors,
      propTypes: shippingSend2ToPropTypes,
    })

  const selectDeliveryPointAddress = address => {
    formik.setValues(values => ({
      ...values,
      addressState: ADDRESS_STATES.AT_PICKUP_POINT,
      addressBook: null,
      selectedDeliveryPoint: address,
      address: {
        ...values.address,
        country: {
          code: address?.detailedAddress?.country_code ?? 'ES',
          label: new Intl.DisplayNames(['es'], { type: 'region' }).of(
            address?.detailedAddress?.country_code ?? 'ES',
          ),
        },
        name: '',
        contactName: address?.name ?? '',
        address: address?.detailedAddress?.address ?? '',
        phone: '',
        phonePrefix: DEFAULT_PHONE_PREFIX,
        email: '',
        stair: '',
        floor: '',
        letter: '',
        postalCode: address?.detailedAddress?.postal_code ?? '',
        province: '',
        city: address?.detailedAddress?.address_2 ?? '',
        hasSeveralLocalities: false,
        localitiesOptions: null,
        schedule: address?.schedule ?? null,
        organicPointCode: address?.organicPointCode ?? '',
      },
      comments: '',
    }))
  }

  const handleChangeBookAddress = async direction => {
    const dirObject = {
      id: direction?.id ?? '',
      email: direction?.email ?? '',
      name: direction?.name ?? '',
      phone: getPhoneInfo(direction?.phone)?.phone ?? '',
      postalCode: direction?.postalCode ?? '',
      phonePrefix: getPhoneInfo(direction?.phone)?.prefix ?? '',
      floor: '',
      letter: '',
      stair: '',
      country: {
        code: direction?.country,
        label: countries.find(({ code }) => code === direction?.country)?.label,
      },
      address: direction?.address ?? '',
      province: direction?.province ?? '',
      city: direction?.city,
      hasSeveralLocalities: false,
      localitiesOptions: null,
    }

    formik.setValues(values => ({
      ...values,
      //selectedAddress: null,
      address: dirObject,
      addressBook: {
        id: direction.id,
        name: direction.name,
        isAddressBook: true,
        shippingAddress: {...direction, ...dirObject},
      },
    }))
  }

  const [defaultCentre, ...restCentres] = centres
  const addressList = [
    formatCentreAddress(defaultCentre, true),
    ...restCentres.map(centre => formatCentreAddress(centre, false)),
  ]
    .filter(address => address !== null)
    .filter(
      address =>
        (address.id !== originCentreId ||
          originSource !== 'clientCentersList') &&
        address.id !== '',
    )

  const addressListAll = (
    !centres?.length
      ? []
      : [
          formatCentreAddress(defaultCentre, true),
          ...restCentres.map(centre => formatCentreAddress(centre, false)),
        ]
  ).filter(address => address !== null)

  const selectAddress = address => {
    const addressObject = {
      id: address?.id,
      autocompleteAddress: address?.autocompleteAddress,
      isDefault: address?.isDefault,
      email: address?.email,
      name: address?.name,
      phone: getPhoneInfo(address?.phone)?.phone ?? '',
      postalCode: address?.postalCode,
      phonePrefix: getPhoneInfo(address?.phone)?.prefix ?? '',
      floor: '',
      letter: '',
      stair: '',
      country: address?.country,
      address: address?.address,
      province: address?.province,
      city: address?.city,
      hasSeveralLocalities: false,
      localitiesOptions: null,
    }
    formik.setValues(values => ({
      ...values,
      address: addressObject,
      selectedAddress: {
        shippingAddress: addressObject,
      },
    }))
  }

  const debouncedPostalCode = useDebounce(
    formik.values.address?.postalCode,
    DEBOUNCED_POSTAL_CODE_DELAY,
  )

  useEffect(() => {
    if (formik.values.addressState === ADDRESS_STATES.EXISTING) {
      if (formik.values?.selectedAddress) {
        formik.setValues(values => ({
          ...values,
          address: formik.values?.selectedAddress?.shippingAddress,
          selectedAddress: formik.values?.selectedAddress,
        }))
      }
    }
  }, [formik.values.addressState])

  useEffect(() => {
    if (
      addressList?.length &&
      !formik?.values?.selectedAddress &&
      !formik?.values?.newAddress &&
      !formik?.values?.addressBook
    ) {
      formik.setValues(values => ({
        ...values,
        address: {
          ...addressList[0],
        },
        selectedAddress: {
          shippingAddress: addressList[0],
        },
      }))
    }
  }, [])

  useEffect(() => {
    if (
      !initialLoad &&
      debouncedPostalCode.trim() !== '' &&
      ((formik.values.address?.country?.code !== '' &&
        formik.values.address?.country?.code !== undefined) ||
        formik.values.address?.country) &&
      debouncedPostalCode === formik.values?.address?.postalCode &&
      (!formik.values?.address?.city || !formik.values?.address?.province) &&
      formik?.values?.addressBook?.shippingAddress?.postalCode !==
        debouncedPostalCode
    ) {
      calculateProvinceAndLocalities(
        debouncedPostalCode,
        formik.values.address?.country?.code,
        fromSearch,
      )
        .then(result => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...result,
              city: result?.city?.length
                ? result?.city
                : values.address?.city ?? '',
              province: result?.province?.length
                ? result?.province
                : values.address?.province ?? '',
            },
          }))

          setZeroResultsGeocode(false)

          const errors = { ...formik.errors }
          if (formik.errors.address?.postalCode)
            delete errors.address?.postalCode

          if (formik.errors.address?.city) delete errors.address.city

          if (formik.errors.address?.province) delete errors.address.province

          formik.setErrors(errors)
        })
        .catch(error => {
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              city: values.address?.city ?? '',
              province: values.address?.province ?? '',
              hasSeveralLocalities: false,
              localitiesOptions: null,
            },
          }))

          if (error.code === GEOCODING_ERRORS.zeroResults) {
            setZeroResultsGeocode(true)
          }
        })
    } else {
      setInitialLoad(false)
    }
  }, [debouncedPostalCode, formik.values.address?.country, fromSearch])

  useEffect(() => {
    if (hasConfirmation && !hasAnyError) onSubmitSuccess({ ...formik.values })
  }, [enhancedFormik, formik, hasAnyError, hasConfirmation])

  useEffect(() => {
    formik.validateForm(formik.values)
  }, [
    formik?.values?.addressBook,
    formik?.values?.selectedAddress,
    formik?.values?.addressState,
  ])
  return (
    <ShippingSendForm onSubmit={enhancedFormik.handleSubmit}>
      <Step2DestinationUI
        countries={countries}
        originCentreId={originCentreId}
        originSource={originSource}
        centreCode={centreCode}
        values={formik.values}
        setValues={formik.setValues}
        touched={formik.touched}
        validate={formik.validateForm}
        handleChangeBookAddress={handleChangeBookAddress}
        zeroResultsGeocode={zeroResultsGeocode}
        fieldErrors={enhancedFormik.fieldErrors}
        formErrors={enhancedFormik.formErrors}
        onChange={e => {
          if (e.target.name === 'addressState') formik.setErrors({})
          setFromSearch(false)
          enhancedFormik.handleChange(e)
        }}
        setErrors={formik.setErrors}
        autofillAddress={addressInfo => {
          setFromSearch(addressInfo?.place)
          formik.setValues(values => ({
            ...values,
            address: {
              ...values.address,
              ...addressInfo,
            },
          }))
        }}
        onBlur={e => {
          formik.validateField(e.target.name)
        }}
        isCenterWithAddressesBook
        addressList={addressListAll}
        selectAddress={selectAddress}
        changeDeliveryPointaddress={changeDeliveryPointAddress}
        deliveryPoints={deliveryPoints.data ?? []}
        selectDeliveryPointAddress={selectDeliveryPointAddress}
        selectedDeliveryPoint={formik.values.selectedDeliveryPoint}
        searchedAddress={formik.values.deliveryPointAddress}
        confirmAddress={confirmAddress}
        confirmedAddress={formik.values.confirmedAddress}
        handlePickUpErrors={handlePickUpErrors}
        isPudo={isPudo}
      />

      <ActionButtons
        isPrimaryDisabled={
          (formik.values.addressState === ADDRESS_STATES.AT_PICKUP_POINT &&
            formik.values.confirmedAddress === undefined) ||
          (formik.values.addressState === ADDRESS_STATES.AT_PICKUP_POINT &&
            formik.values.address.email === '') ||
          /*(formik.values.addressState === ADDRESS_STATES.EXISTING &&
            centres.find(({ id }) => formik.values.address.id === id) &&
            formik.values.address.phone === '') ||*/
          error ||
          Object.keys(formik.errors?.address ?? {})?.length > 0
        }
        isDirty={formik.dirty}
        isSaved={savedValues !== null}
        onCancel={onCancel}
        hasOtherError={hasOtherError}
        hasServerError={hasServerError}
        currentStep={STEPS.TO}
        activeStep={activeStep}
      />

      {hasConfirmation && hasAnyError ? (
        <ConfirmationWithErrorModal
          onDiscard={handleDiscardConfirmation}
          onAmend={onCancelConfirmation}
        />
      ) : null}
    </ShippingSendForm>
  )
}

Step2Destination.propTypes = {
  centreCode: PropTypes.string.isRequired,
  savedValues: nullablePropType(
    makeValuesPropType(shippingSend2ToPropTypes).isRequired,
  ),
  activeStep: PropTypes.oneOf(Object.values(STEPS)).isRequired,
  serverFieldErrors: nullablePropType(
    makeFieldErrorsPropType(shippingSend2ToPropTypes).isRequired,
  ),
  hasOtherError: PropTypes.bool.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSetDirty: PropTypes.func.isRequired,
  hasConfirmation: PropTypes.bool.isRequired,
  onCancelConfirmation: PropTypes.func.isRequired,
  onAcceptConfirmation: PropTypes.func.isRequired,
  isPudo: PropTypes.bool,
}

export default Step2Destination
